'use client';

import React, {
  ButtonHTMLAttributes,
  ReactNode,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useFocusable } from '@noriginmedia/norigin-spatial-navigation';

import { useCursorDetection } from '@/hooks/useCursorDetection';

import { getSystem } from '@/system';
import { TypographyVariant, typographyVariantClasses } from '@/utils/typographyVariants';

import { useAccessibility } from '../Accessibility/AccessibilityContext';

type ButtonVariant = 'default' | 'focused' | 'disabled' | 'transparent' | 'selected';
type ButtonSize = 'long' | 'small' | 'medium' | 'large' | 'auto' | 'promo';

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  children: ReactNode;
  className?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  iconPosition?: 'left' | 'right';
  hoverIcon?: ReactNode;
  disabledIcon?: ReactNode;
  onArrowPress?: (direction: string) => void;
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  focused?: boolean;
  focusKey?: string;
  selected?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  focusedStyleClasses?: string;
  selectedStyleClasses?: string;
  showLabelOnFocus?: boolean;
  focusOnHover?: boolean;
  focusable?: boolean;
  typographyVariant?: TypographyVariant;
};

const variantStyles: Record<ButtonVariant, string> = {
  default: 'bg-buttons-secondary-color text-buttons-secondary-text',
  transparent: 'bg-transparent text-buttons-secondary-text',
  focused: 'bg-buttons-primary-color text-buttons-primary-text',
  disabled: 'bg-buttons-disabled-color text-buttons-disabled-text cursor-not-allowed',
  selected: 'bg-core-elements-outline-border text-buttons-secondary-text',
};

const sizeStyles: Record<ButtonSize, string> = {
  long: 'w-[30.5rem] h-1100 border-none',
  small: 'w-32 h-[5.313rem]',
  medium: 'w-44 h-[5.313rem]',
  large: 'w-128 h-[5.313rem]',
  auto: 'w-auto h-[5.313rem]',
  promo: 'w-auto min-w-[14.375rem] max-w-[16rem] h-[5rem] !px-4',
};

const baseStyles =
  'flex items-center text-center text-callout leading-[1.875rem] font-bold not-italic justify-center rounded-2xl focus:outline-none py-6 px-8';

const system = getSystem();

const Button: React.FC<ButtonProps> = React.memo((props) => {
  const {
    children,
    onClick,
    onArrowPress,
    disabled = false,
    className = '',
    type = 'button',
    variant = 'default',
    size = 'medium',
    icon,
    iconPosition = 'left',
    hoverIcon,
    disabledIcon,
    'aria-label': ariaLabel,
    focusKey,
    onFocus,
    onBlur,
    selected,
    focusedStyleClasses = '',
    selectedStyleClasses = '',
    showLabelOnFocus = false,
    focusOnHover = true,
    focusable = true,
    typographyVariant,
    onMouseOver: userOnMouseOver,
    ...rest
  } = props;

  const [isHovered, setIsHovered] = useState(false);
  const { announceToScreenReader } = useAccessibility();

  const { ref, focused, focusSelf } = useFocusable({
    focusable: focusable && !disabled,
    focusKey,
    onFocus: () => {
      if (!system.overrideAria) ref.current?.focus();
      if (ariaLabel) announceToScreenReader(ariaLabel);
      onFocus?.();
    },
    onBlur: () => {
      if (ariaLabel) announceToScreenReader('');
      if (!system.overrideAria) ref.current?.blur();
      onBlur?.();
    },
    onEnterPress: () => {
      if (!disabled) onClick?.();
    },
    onArrowPress: (direction) => onArrowPress?.(direction) ?? true,
  });

  const { isUsingCursor } = useCursorDetection();

  const isFocusedOrHovered = focusOnHover
    ? focused
    : (!isUsingCursor && focused) || (isUsingCursor && isHovered);

  const labelRef = useRef<HTMLSpanElement>(null);
  const [labelExpanded, setLabelExpanded] = useState(false);

  useLayoutEffect(() => {
    if (!showLabelOnFocus) return;
    if (!isFocusedOrHovered) {
      setLabelExpanded(false);
      return;
    }
    // Older WPE WebKit (Xi6) only starts transitions if the previous style (with the transition set) was applied during a style recalculation. 
    // We now orce that recalculation  while the element is still collapsed, then expand it immediately to avoid flickering when moving the focus to the righ hand buttons.
    void labelRef.current?.offsetWidth;
    setLabelExpanded(true);
  }, [showLabelOnFocus, isFocusedOrHovered]);

  const buttonStyles = useMemo(() => {
    const styles = [baseStyles];

    if (disabled) {
      styles.push(variantStyles.disabled);
    } else if (isFocusedOrHovered) {
      styles.push(variantStyles.focused, focusedStyleClasses);
    } else if (selected) {
      styles.push(variantStyles.selected, selectedStyleClasses);
    } else {
      styles.push(variantStyles[variant]);
    }

    if (typographyVariant) {
      styles.push(typographyVariantClasses[typographyVariant]);
    }

    styles.push(sizeStyles[size]);
    if (className) {
      styles.push(className);
    }

    return styles.join(' ');
  }, [
    disabled,
    focused,
    selected,
    variant,
    size,
    className,
    focusedStyleClasses,
    selectedStyleClasses,
    isHovered,
  ]);

  const currentIcon = useMemo(() => {
    if (disabled) return disabledIcon;
    if (isFocusedOrHovered && !disabled) return hoverIcon;
    return icon;
  }, [disabled, isHovered, focused, icon, hoverIcon, disabledIcon]);

  return (
    <button
      ref={ref}
      onClick={!disabled ? onClick : undefined}
      type={type}
      onMouseOver={(e) => {
        setIsHovered(true);
        if (focusable && focusOnHover) {
          focusSelf();
        }
        userOnMouseOver?.(e);
      }}
      disabled={disabled}
      onMouseLeave={() => setIsHovered(false)}
      className={buttonStyles}
      aria-label={ariaLabel || ''}
      aria-disabled={disabled}
      {...rest}
    >
      {iconPosition === 'left' ? currentIcon : null}
      {showLabelOnFocus ? (
        <span
          ref={labelRef}
          className={`inline-block overflow-hidden ${
            isFocusedOrHovered
              ? 'transition-[max-width] duration-[720ms]'
              : 'transition-none'
          } ${labelExpanded ? 'max-w-[16rem] opacity-100' : 'max-w-0 opacity-0'}`}
          aria-hidden={!isFocusedOrHovered}
        >
          <span
            className={`block ${
              isFocusedOrHovered
                ? 'transition-transform duration-[720ms]'
                : 'transition-none'
            } ${labelExpanded ? 'translate-x-0' : 'translate-x-2'}`}
          >
            {children}
          </span>
        </span>
      ) : (
        children
      )}
      {iconPosition === 'right' ? currentIcon : null}
    </button>
  );
});

Button.displayName = 'Button';

export default Button;
